import { useCallback, useEffect, useRef, useState } from "react";
import { api, BACKEND } from "@/lib/api";

export function useAuction({ club, onNotification, onBid } = {}) {
  const [snap, setSnap] = useState(null);
  const [connected, setConnected] = useState(false);
  const [offset, setOffset] = useState(0);
  const [lastEvent, setLastEvent] = useState(null);
  const cbs = useRef({ onNotification, onBid });
  cbs.current = { onNotification, onBid };
  const [wheel, setWheel] = useState(null);

  const apply = useCallback((s, event) => {
    setSnap(s);
    setOffset(new Date(s.server_time).getTime() - Date.now());
    if (event) setLastEvent({ event, at: Date.now() });
  }, []);

  const refresh = useCallback(async () => {
    const r = await api.get(`/auction/${club}/state`);
    apply(r.data);
  }, [apply, club]);

  useEffect(() => {
    if (!club) return undefined;
    let ws, retry = 0, alive = true, pingT, reconT;
    const connect = () => {
      ws = new WebSocket(BACKEND.replace(/^http/, "ws") + `/api/ws?club=${club}`);
      ws.onopen = () => {
        retry = 0;
        setConnected(true);
        pingT = setInterval(() => ws.readyState === 1 && ws.send("ping"), 15000);
      };
      ws.onmessage = (m) => {
        const msg = JSON.parse(m.data);
        if (msg.type === "state") apply(msg.snapshot, msg.event);
        if (msg.type === "state" && msg.event === "nominate" && msg.random) setWheel({ names: msg.wheel, at: Date.now() });
        if (msg.type === "notification") cbs.current.onNotification?.(msg.notification);
        if (msg.type === "bid") cbs.current.onBid?.(msg.bid);
      };
      ws.onclose = () => {
        setConnected(false);
        clearInterval(pingT);
        if (alive) reconT = setTimeout(connect, Math.min(10000, 500 * 2 ** retry++));
      };
      ws.onerror = () => ws.close();
    };
    refresh().catch(() => {});
    connect();
    const poll = setInterval(() => {
      if (!ws || ws.readyState !== 1) refresh().catch(() => {});
    }, 4000);
    return () => {
      alive = false;
      clearInterval(poll);
      clearInterval(pingT);
      clearTimeout(reconT);
      ws && ws.close();
    };
  }, [apply, refresh, club]);

  return { snap, connected, offset, refresh, lastEvent, setSnap: apply, wheel };
}

export function useRemaining(endsAt, offset) {
  const [rem, setRem] = useState(null);
  useEffect(() => {
    if (!endsAt) {
      setRem(null);
      return;
    }
    const tick = () => setRem(Math.max(0, (new Date(endsAt).getTime() - (Date.now() + offset)) / 1000));
    tick();
    const t = setInterval(tick, 200);
    return () => clearInterval(t);
  }, [endsAt, offset]);
  return rem;
}
